<script lang="ts">
  import MobileCorridorMap from "./MobileCorridorMap.svelte";
  import { Minus, Plus, Scan } from "lucide-svelte";
  import { BRIDGE_STATIONS } from "../../shared/corridor";
  import {
    RIVER_MOUTH_S_METERS,
    bridgeStation,
    routeDiagramPoint,
    svgRoutePath,
  } from "../../shared/river-model";
  import type { BridgeId, PublicBridge, PublicSnapshot, PublicVesselGroup, PublicVesselSummary } from "../../shared/contracts";
  import { bridgeCopy, bridgePositionCopy, formatEtTimestamp, freshnessLabel } from "../format";
  import { clamp } from "../geometry";
  import {
    likelyTowRelationship,
    movementOrdinals,
    trafficMovements,
    type LikelyTowMovement,
  } from "../tow-groups";
  import BridgeStation from "./BridgeStation.svelte";
  import { layoutVesselDiagram, diagramEstimatePath } from "../vessel-diagram-layout";
  import TowLink from "./TowLink.svelte";
  import VesselMarker from "./VesselMarker.svelte";

  export let compact = false;
  export let active = true;
  export let selectedBridgeId: BridgeId = "brickell";
  export let viewRevision = 0;
  export let bridges: PublicBridge[];
  export let vessels: PublicVesselSummary[];
  export let vesselGroups: PublicVesselGroup[] = [];
  export let selectedVesselId: string | null = null;
  export let dataMode: PublicSnapshot["dataMode"] = "live";
  export let onvesselselect: (vessel: PublicVesselSummary, trigger: HTMLElement) => void;

  type Point = { x: number; y: number };
  type StationPlacement = Point & {
    labelX: number;
    labelY: number;
    labelSide: -1 | 1;
  };

  const MAP_WIDTH = 1320;
  const MAP_HEIGHT = 640;
  const MAX_ZOOM = 6;
  const BRICKELL = bridgeStation("brickell").diagram;
  const MOUTH = routeDiagramPoint("miami_river", RIVER_MOUTH_S_METERS, 0);
  const MAIN_ROUTE = svgRoutePath("miami_river");
  const NORTH_ROUTE = svgRoutePath("north_approach");
  const CUT_ROUTE = svgRoutePath("government_cut");
  const SOUTH_ROUTE = svgRoutePath("south_approach");

  const stationLayout: Record<BridgeId, StationPlacement> = {
    brickell: { ...BRICKELL, labelX: 0, labelY: 0, labelSide: 1 },
    "sw-2-ave": { ...bridgeStation("sw-2-ave").diagram, labelX: -2, labelY: 58, labelSide: -1 },
    "sw-1-st": { ...bridgeStation("sw-1-st").diagram, labelX: 3, labelY: -68, labelSide: 1 },
    "w-flagler": { ...bridgeStation("w-flagler").diagram, labelX: -2, labelY: 58, labelSide: -1 },
    "nw-5-st": { ...bridgeStation("nw-5-st").diagram, labelX: 3, labelY: -68, labelSide: 1 },
    "nw-12-ave": { ...bridgeStation("nw-12-ave").diagram, labelX: -2, labelY: 58, labelSide: -1 },
    "nw-17-ave": { ...bridgeStation("nw-17-ave").diagram, labelX: 3, labelY: -68, labelSide: 1 },
    "nw-22-ave": { ...bridgeStation("nw-22-ave").diagram, labelX: -2, labelY: 58, labelSide: -1 },
    "nw-27-ave": { ...bridgeStation("nw-27-ave").diagram, labelX: 28, labelY: -46, labelSide: 1 },
  };

  let zoom = 1;
  let panX = 0;
  let panY = 0;
  let dragging = false;
  let pointerId: number | null = null;
  let lastX = 0;
  let lastY = 0;

  $: bridgeLookup = new Map(bridges.map((bridge) => [bridge.id, bridge]));
  $: plottedStations = [
    ...BRIDGE_STATIONS.filter((station) => station.id === "brickell"),
    ...BRIDGE_STATIONS.filter((station) => station.id !== "brickell").reverse(),
  ];
  $: movements = trafficMovements(vessels, vesselGroups);
  $: ordinals = movementOrdinals(movements);
  $: likelyTowMovements = movements.filter(
    (movement): movement is LikelyTowMovement => movement.kind === "likely_tow",
  );
  $: towMovementByVesselId = new Map(
    likelyTowMovements.flatMap((movement) =>
      movement.vessels.map((vessel) => [vessel.id, movement] as const),
    ),
  );
  $: selectedTowGroupId = selectedVesselId
    ? towMovementByVesselId.get(selectedVesselId)?.group.id ?? null
    : null;
  $: displayTowVesselById = layoutVesselDiagram(vessels, vesselGroups);

  function fitAll(): void {
    zoom = 1;
    panX = 0;
    panY = 0;
  }

  function changeZoom(delta: number): void {
    zoom = clamp(zoom + delta, 1, MAX_ZOOM);
    if (zoom === 1) fitAll();
  }

  function pointerDown(event: PointerEvent): void {
    if (event.button !== 0 || zoom <= 1) return;
    const target = event.currentTarget as SVGSVGElement;
    pointerId = event.pointerId;
    target.setPointerCapture(event.pointerId);
    dragging = true;
    lastX = event.clientX;
    lastY = event.clientY;
  }

  function pointerMove(event: PointerEvent): void {
    if (!dragging || event.pointerId !== pointerId) return;
    const target = event.currentTarget as SVGSVGElement;
    const bounds = target.getBoundingClientRect();
    const scale = Math.max(MAP_WIDTH / Math.max(1, bounds.width), MAP_HEIGHT / Math.max(1, bounds.height));
    panX += (event.clientX - lastX) * scale;
    panY += (event.clientY - lastY) * scale;
    lastX = event.clientX;
    lastY = event.clientY;
  }

  function pointerUp(event: PointerEvent): void {
    if (event.pointerId !== pointerId) return;
    dragging = false;
    pointerId = null;
  }

  function wheel(event: WheelEvent): void {
    event.preventDefault();
    changeZoom(event.deltaY < 0 ? 0.4 : -0.4);
  }

  function vesselPosition(vessel: PublicVesselSummary, linked = false): Point {
    return routeDiagramPoint(
      linked && vessel.sMeters >= RIVER_MOUTH_S_METERS ? "miami_river" : vessel.corridor,
      vessel.sMeters,
      vessel.corridorProgress,
    );
  }

  function groupLinkPoints(movement: LikelyTowMovement): Point[] {
    return movement.vessels
      .map(displayVessel)
      .sort((a, b) => a.sMeters - b.sMeters)
      .map((vessel) => vesselPosition(vessel, true));
  }

  function displayVessel(vessel: PublicVesselSummary): PublicVesselSummary {
    return displayTowVesselById.get(vessel.id) ?? vessel;
  }


</script>

<section class:dragging class="corridor" aria-label="Live bridge corridor">
  <div class="sr-only" aria-live="polite">
    <h2>Live bridge positions</h2>
    <ol>
      {#each BRIDGE_STATIONS as station (station.id)}
        {@const bridge = bridgeLookup.get(station.id)}
        {#if bridge}
          <li>
            {#if station.id === "brickell"}
              {station.name}: {bridgeCopy[bridge.state].label}, {bridgeCopy[bridge.state].consequence}.
            {:else}
              {station.name}: {bridgePositionCopy[bridge.state]}.
            {/if}
            {freshnessLabel(bridge.freshness, dataMode)}. Observed {formatEtTimestamp(bridge.observedAt)}.
            Last confirmed {formatEtTimestamp(bridge.lastConfirmedAt)}.
          </li>
        {/if}
      {/each}
    </ol>
  </div>

  {#if compact}
    <MobileCorridorMap {active} {bridges} {vessels} {vesselGroups} {selectedVesselId} {selectedBridgeId} {viewRevision} {dataMode} {onvesselselect} />
  {:else}
  <div class="corridor__tools" aria-label="Map controls">
    <button type="button" disabled={zoom >= MAX_ZOOM} onclick={() => changeZoom(0.5)} aria-label="Zoom in"><Plus size={18} /></button>
    <button type="button" disabled={zoom <= 1} onclick={() => changeZoom(-0.5)} aria-label="Zoom out"><Minus size={18} /></button>
    <button class="corridor__fit" type="button" disabled={zoom === 1 && panX === 0 && panY === 0} onclick={fitAll}><Scan size={17} /><span>Fit all</span></button>
  </div>

  <div class="corridor__viewport">
    <svg
      class="corridor__svg"
      viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
      role="group"
      aria-labelledby="corridor-map-title corridor-map-description"
      onpointerdown={pointerDown}
      onpointermove={pointerMove}
      onpointerup={pointerUp}
      onpointercancel={pointerUp}
      onwheel={wheel}
    >
      <title id="corridor-map-title">Live Miami River bridge atlas</title>
      <desc id="corridor-map-description">Eight bridges continue upriver to the left of Brickell. The river mouth and three Biscayne Bay approaches continue downriver to the right.</desc>
      <defs>
        <pattern id="atlas-grid" width="64" height="64" patternUnits="userSpaceOnUse">
          <path d="M64 0H0V64" fill="none" />
          <circle cx="0" cy="0" r="1" />
        </pattern>
        <pattern id="water-detail" width="256" height="256" patternUnits="userSpaceOnUse">
          <image href="/assets/bridges/water-detail.webp" width="256" height="256" opacity="0.22" />
        </pattern>
      </defs>

      <g
        class="corridor__world"
        transform={`translate(${MAP_WIDTH / 2 + panX} ${MAP_HEIGHT / 2 + panY}) scale(${zoom}) translate(${-MAP_WIDTH / 2} ${-MAP_HEIGHT / 2})`}
      >
        <rect class="atlas-paper" width={MAP_WIDTH} height={MAP_HEIGHT} />
        <rect class="atlas-grid" width={MAP_WIDTH} height={MAP_HEIGHT} />

        <g class="route-network" aria-hidden="true">
          <g class="route route--main">
            <path class="route__water" d={MAIN_ROUTE} />
            <path class="route__texture" d={MAIN_ROUTE} />
            <path class="route__casing" d={MAIN_ROUTE} />
            <path class="route__line" d={MAIN_ROUTE} />
          </g>
          <g class="route"><path class="route__water" d={NORTH_ROUTE} /><path class="route__texture" d={NORTH_ROUTE} /><path class="route__casing" d={NORTH_ROUTE} /><path class="route__line" d={NORTH_ROUTE} /></g>
          <g class="route"><path class="route__water" d={CUT_ROUTE} /><path class="route__texture" d={CUT_ROUTE} /><path class="route__casing" d={CUT_ROUTE} /><path class="route__line" d={CUT_ROUTE} /></g>
          <g class="route"><path class="route__water" d={SOUTH_ROUTE} /><path class="route__texture" d={SOUTH_ROUTE} /><path class="route__casing" d={SOUTH_ROUTE} /><path class="route__line" d={SOUTH_ROUTE} /></g>
        </g>

        <g class="atlas-direction atlas-direction--up" aria-hidden="true">
          <text x="32" y="582">UPRIVER</text><path d="M34 594H100 M34 594l14 -9 M34 594l14 9" />
        </g>
        <g class="atlas-direction atlas-direction--down" aria-hidden="true">
          <text x="1080" y="72">DOWNRIVER</text><path d="M1080 84h126 M1206 84l-14 -9 M1206 84l-14 9" />
        </g>
        <text class="atlas-bay" x="1080" y="42">BISCAYNE BAY</text>
        <text class="atlas-river-name" x="660" y="575" text-anchor="middle">MIAMI RIVER</text>

        <g class="branch-labels" aria-hidden="true">
          <text x="1238" y="104" text-anchor="end">ICW NORTH</text>
          <text x="1252" y="352" text-anchor="end">GOVERNMENT CUT</text>
          <text x="1244" y="548" text-anchor="end">ICW SOUTH</text>
          <text x={MOUTH.x} y={MOUTH.y - 32} text-anchor="middle">RIVER MOUTH</text>
        </g>

        <g class="route-terminals" aria-hidden="true">
          <circle cx="30" cy="80" r="9" />
          <circle cx="1280" cy="130" r="9" />
          <circle cx="1280" cy="320" r="9" />
          <circle cx="1280" cy="510" r="9" />
          <circle class="route-junction" cx={MOUTH.x} cy={MOUTH.y} r="13" />
        </g>

        <g class="compass" transform="translate(1255 48)" aria-hidden="true">
          <circle r="18" /><path d="M0 -30V30M-30 0H30M-19 -19L19 19M19 -19L-19 19" /><path class="compass__north" d="M0 -28l-5 18L0 -14l5 4z" /><text y="-35" text-anchor="middle">N</text>
        </g>

        {#each likelyTowMovements as movement (movement.id)}
          <TowLink
            groupId={movement.group.id}
            points={groupLinkPoints(movement)}
            selected={selectedTowGroupId === movement.group.id}
          />
        {/each}

        {#each plottedStations as station (station.id)}
          {@const bridge = bridgeLookup.get(station.id)}
          {@const position = stationLayout[station.id]}
          {#if bridge}
            <BridgeStation
              {station}
              {bridge}
              x={position.x}
              y={position.y}
              variant={Math.max(0, BRIDGE_STATIONS.findIndex((candidate) => candidate.id === station.id) - 1) % 3}
              focal={station.id === "brickell"}
              labelX={position.labelX}
              labelY={position.labelY}
              labelSide={position.labelSide}
              {dataMode}
            />
          {/if}
        {/each}

        {#each vessels as vessel, index (vessel.id)}
          {@const towMovement = towMovementByVesselId.get(vessel.id)}
          {@const reportedPoint = vesselPosition(vessel)}
          {@const displayPoint = vesselPosition(displayVessel(vessel))}
          {#if Math.hypot(reportedPoint.x - displayPoint.x, reportedPoint.y - displayPoint.y) > 0.5}
            <g aria-hidden="true" class="vessel-estimate">
              <path d={diagramEstimatePath(vessel, displayVessel(vessel))} fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="4 4" />
              <circle cx={reportedPoint.x} cy={reportedPoint.y} r="4" fill="currentColor"><title>Reported position</title></circle>
            </g>
          {/if}
          <VesselMarker
            {vessel}
            point={vesselPosition(displayVessel(vessel), towMovement !== undefined)}
            ordinal={ordinals.get(vessel.id) ?? index + 1}
            selected={selectedVesselId === vessel.id}
            groupSelected={towMovement !== undefined && selectedTowGroupId === towMovement.group.id}
            linked={towMovement !== undefined}
            compact={towMovement !== undefined}
            relationship={towMovement ? likelyTowRelationship(vessel, towMovement) : null}
            onselect={onvesselselect}
          />
        {/each}
      </g>
    </svg>
  </div>

  <p class="corridor__gesture">Use controls to zoom · drag to move</p>
  {/if}
</section>
